/**
 * StrategyLegend — lists project strategies with color swatch + visibility
 * checkbox. In Mode A the legend collapses to a single read-only badge;
 * in Mode B it lets the user toggle strategies on/off and "solo" one.
 */

import React from 'react';
import {
  Box, Checkbox, FormControlLabel, FormControl, IconButton, MenuItem,
  Select, Stack, Tooltip, Typography,
} from '@mui/material';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { CONTENT_PLAN } from '@test-kit/selectors/content-plan';
import type { CalendarAwareContentStrategy } from '../../types';

export interface StrategyLegendEntry {
  strategy: CalendarAwareContentStrategy;
  color: string;
}

export interface StrategyLegendProps {
  entries: StrategyLegendEntry[];
  visibleIds: Set<string>;
  onToggle: (strategyId: string, visible: boolean) => void;
  onSolo?: (strategyId: string) => void;
  collapsed?: boolean;   // Mode A — show single entry with dropdown selector
  /** Mode A only — full list of strategies in the project + select handler.
   *  When provided, the collapsed legend renders a Select dropdown instead
   *  of a read-only label, so users can switch the active strategy without
   *  leaving the Calendar tab. */
  allStrategies?: CalendarAwareContentStrategy[];
  onSelect?: (strategyId: string) => void;
}

export const StrategyLegend: React.FC<StrategyLegendProps> = ({
  entries, visibleIds, onToggle, onSolo, collapsed = false,
  allStrategies, onSelect,
}) => {
  if (entries.length === 0 && (!allStrategies || allStrategies.length === 0)) return null;

  if (collapsed) {
    const only = entries[0];
    const activeId = only?.strategy.id ?? '';
    // When the panel passes a list of strategies + a selector callback,
    // render a Select dropdown instead of a read-only label so users can
    // switch the active strategy without leaving the Calendar tab.
    if (allStrategies && allStrategies.length > 0 && onSelect) {
      return (
        <Box data-testid={CONTENT_PLAN.CALENDAR_LEGEND}>
          <Typography variant="caption" color="text.secondary">Strategy</Typography>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.25 }}>
            {only && (
              <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: only.color }} />
            )}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={activeId}
                onChange={(e) => onSelect(String(e.target.value))}
                displayEmpty
                inputProps={{ 'data-testid': 'calendar-strategy-select' }}
              >
                {allStrategies.map((s) => {
                  const label = (s as { name?: string; title?: string }).name ?? (s as any).title ?? s.id;
                  return (
                    <MenuItem
                      key={s.id}
                      value={s.id}
                      data-testid={`calendar-strategy-option-${s.id}`}
                    >
                      {label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      );
    }
    if (!only) return null;
    return (
      <Box data-testid={CONTENT_PLAN.CALENDAR_LEGEND}>
        <Typography variant="caption" color="text.secondary">Strategy</Typography>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.25 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: only.color }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {(only.strategy as { name?: string; title?: string }).name ??
             (only.strategy as { title?: string }).title ?? only.strategy.id}
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box data-testid={CONTENT_PLAN.CALENDAR_LEGEND}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Strategies
      </Typography>
      <Stack spacing={0.25}>
        {entries.map(({ strategy, color }) => {
          const visible = visibleIds.has(strategy.id);
          const label = (strategy as { name?: string; title?: string }).name ??
                        (strategy as { title?: string }).title ??
                        strategy.id;
          return (
            <Box
              key={strategy.id}
              data-testid={CONTENT_PLAN.CALENDAR_LEGEND_ROW}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    data-testid={CONTENT_PLAN.CALENDAR_LEGEND_TOGGLE}
                    size="small"
                    checked={visible}
                    onChange={(e) => onToggle(strategy.id, e.target.checked)}
                    icon={<RadioButtonUncheckedIcon fontSize="small" sx={{ color }} />}
                    checkedIcon={<RadioButtonCheckedIcon fontSize="small" sx={{ color }} />}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: visible ? 600 : 400 }}>
                    {label}
                  </Typography>
                }
                sx={{ m: 0, flex: 1 }}
              />
              {onSolo && (
                <Tooltip title="Show only this strategy" placement="top">
                  <IconButton
                    data-testid={CONTENT_PLAN.CALENDAR_LEGEND_SOLO}
                    size="small"
                    onClick={() => onSolo(strategy.id)}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 10 }}>S</Typography>
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};
